import React, { useEffect, useRef, useState } from "react";
import { usePostFileMutation } from "@/store/service";
import { FileText, FileImage, File, Upload, Trash2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface FileUploaderProps {
  title?: string;
  subtitle?: string;
  entityType?: string;
  entityId?: string;
  onFileUploaded: (allFiles: File[]) => void;
}

const FileMappings: { icon: React.ElementType; check: (type: string) => boolean }[] = [
  { icon: Upload, check: (type: string) => ["application/pdf"].includes(type) },
  { icon: FileImage, check: (type: string) => type.includes("image") },
  { icon: FileText, check: () => true },
];

function prettyPrintFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

const FileUploader: React.FC<FileUploaderProps> = ({
  title = "Upload documents",
  subtitle,
  entityType,
  entityId,
  onFileUploaded,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const [postFileMutation, { isLoading, error }] = usePostFileMutation();
  const inputRef = useRef<HTMLInputElement>(null);

  const generateFileIcon = (type: string) => {
    const correspondItem = FileMappings.find((entry) => entry.check(type));
    const Icon = correspondItem?.icon ?? File;
    return <Icon className="size-5 text-gray-500 mr-3 flex-shrink-0" />;
  };

  useEffect(() => {
    onFileUploaded(selectedFiles);
  }, [selectedFiles]);

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      handleUpload(files[0]);
    }
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    handleUpload(file);
  };

  const handleUpload = (file: File) => {
    postFileMutation({ file, entityId, entity: entityType })
      .unwrap()
      .then(() => setSelectedFiles([...selectedFiles, file]))
      .catch(() => {});
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{title}</label>
      {subtitle && <p className="text-xs text-gray-500 mb-2">{subtitle}</p>}

      <div
        className={cn(
          "relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors",
          isDragging ? "border-[#0FD131] bg-green-50" : "border-gray-300 bg-gray-50 hover:border-gray-400",
        )}
        onClick={() => inputRef.current?.click()}
      >
        {isLoading ? (
          <Loader2 className="size-6 text-gray-400 animate-spin" />
        ) : (
          <>
            <Upload className="size-6 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              {isDragging ? (
                <span className="font-medium">Drop file here</span>
              ) : (
                <span>
                  Drag file here or <span className="font-medium text-[#0FD131]">click to upload</span>
                </span>
              )}
            </p>
          </>
        )}
        <input className="hidden" ref={inputRef} type="file" name="uploader" onChange={handleFileSelected} />
        <div
          className="absolute inset-0"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        />
      </div>
      {typeof error === "string" && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {selectedFiles.length > 0 && (
        <div className="mt-3 space-y-2">
          {selectedFiles.map(({ name, type, size }, idx) => (
            <div
              key={`${size}-${idx}`}
              className="flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2"
            >
              {type && generateFileIcon(type)}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                {size > 0 && <p className="text-xs text-gray-500">{prettyPrintFileSize(size)}</p>}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  removeFile(idx);
                }}
                className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default FileUploader;
